import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";

import { projectsApi } from "@/api/projects.ts";
import { Alert, Button, Card, Field, Input, Select } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { projectQuery } from "./ProjectPage.tsx";

/** Outside the component: a component created during render is a new type each time, so React remounts the whole
 *  box on every keystroke and the field being typed in loses focus. */
const BareBox = ({ children }: { children: React.ReactNode }) => <div className="rounded-lg border border-border p-3">{children}</div>;

export function ProjectMembers({ projectId, bare = false }: { projectId: string; bare?: boolean }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const p = useQuery(projectQuery(projectId)).data;
  const q = useQuery({ queryKey: ["projects", projectId, "members"], queryFn: () => projectsApi.members(projectId) });
  const [form, setForm] = useState({ email: "", role: "editor" as "editor" | "viewer" });
  const refresh = () => qc.invalidateQueries({ queryKey: ["projects", projectId, "members"] });
  const add = useMutation({ mutationFn: () => projectsApi.addMember(projectId, form.email, form.role), onSuccess: () => { setForm({ email: "", role: "editor" }); void refresh(); } });
  const { confirm } = useConfirm();
  const remove = useMutation({ mutationFn: (userId: string) => projectsApi.removeMember(projectId, userId), onSuccess: () => refresh() });
  const askRemove = async (m: { userId: string; name: string }) => { if (await confirm({ title: t("confirm.removeMemberTitle"), message: t("confirm.removeMemberMsg", { name: m.name }), confirmLabel: t("projects.members.remove"), danger: true })) remove.mutate(m.userId); };
  const isOwner = p?.myRole === "owner";
  const Wrap = bare ? BareBox : Card;
  return (
    <div className="space-y-4">
      {isOwner && (
        <Wrap>
          <form className="grid items-end gap-3 sm:grid-cols-[1fr_auto_auto]" onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
            <Field label={t("projects.members.email")}>
              <Input type="email" dir="ltr" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label={t("projects.members.role")}>
              <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as "editor" | "viewer" })}>
                <option value="editor">{t("projects.members.roles.editor")}</option>
                <option value="viewer">{t("projects.members.roles.viewer")}</option>
              </Select>
            </Field>
            <Button type="submit" loading={add.isPending}>{t("projects.members.add")}</Button>
          </form>
          {add.error && <Alert tone="danger" className="mt-3">{toMsg(add.error)}</Alert>}
          <p className="mt-3 text-xs text-fg-muted">{t("projects.members.hint")}</p>
        </Wrap>
      )}
      <Wrap>
        <ul className="divide-y divide-border text-sm">
          {(q.data ?? []).map((m) => (
            <li key={m.userId} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <span className="font-medium">{m.name}</span>
                <span className="ms-2 rounded bg-surface-2 px-1.5 py-0.5 text-xs text-fg-muted">{t(`projects.members.roles.${m.role}`)}</span>
                <div dir="ltr" className="truncate text-start text-xs text-fg-muted">{m.email}</div>
              </div>
              {isOwner && m.role !== "owner" && <Button variant="ghost" size="sm" onClick={() => void askRemove(m)}>{t("projects.members.remove")}</Button>}
            </li>
          ))}
        </ul>
      </Wrap>
    </div>
  );
}
